import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Button } from 'components/ui/Button/Button'
import { NoData } from 'components/NoData'
import {
    closeMonthEnd,
    getContractorStatement,
    getMonthEnd,
    signOffMonthEnd,
} from 'features/fuelManagement/fuelCustodySlice'
import { Flex } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import { formatNumbersForDisplay } from 'utils/helpers'
import { IFuelContractorRechargeRow, IFuelMonthEndTankRow } from 'types/fuelCustody.interface'
import { RootState } from 'store'
import {
    LedgerLabel,
    LedgerOp,
    LedgerRow,
    LedgerValue,
    LedgerWrap,
    SignedText,
    SignOffWrap,
} from './FuelTanks.styled'
import {
    ContinuityNote,
    MemoHeadline,
    MemoNote,
    MemoRow,
    RechargeAmount,
    StatementCellText,
} from './MonthEndPanel.styled'

const fmt = (value: number): string => formatNumbersForDisplay(value)

const signedL = (value: number): string =>
    value === 0 ? '0 L' : `${value > 0 ? '+' : '−'}${fmt(Math.abs(value))} L`

const currentMonth = (): string => format(new Date(), 'yyyy-MM')

export const MonthEndPanel = () => {
    const dispatch = useDispatch()
    const { monthEnd, contractorStatement } = useSelector((store: RootState) => store.fuelCustody)
    const month = currentMonth()

    useEffect(() => {
        dispatch(getMonthEnd({ month }) as any)
        dispatch(getContractorStatement({ month }) as any)
    }, [dispatch, month])

    const handlePrepare = async () => {
        try {
            await dispatch(closeMonthEnd({ month }) as any)
            await dispatch(getMonthEnd({ month }) as any)
            toast.success('Month-end statement prepared')
        } catch {
            toast.error('Could not prepare month-end — ensure all recon days are signed off')
        }
    }

    const handleSignOff = async () => {
        if (!monthEnd?.id) return
        try {
            await dispatch(signOffMonthEnd(monthEnd.id) as any)
            await dispatch(getMonthEnd({ month }) as any)
        } catch {
            toast.error('Could not sign off month-end')
        }
    }

    if (!monthEnd) {
        return (
            <Flex column gap='1rem' width='100%'>
                <NoData text='No month-end statement yet — sign off all daily reconciliations then prepare the close pack.' />
                <Button variant='primary' size='sm' onClick={handlePrepare}>
                    Prepare {month} close
                </Button>
            </Flex>
        )
    }

    const close = monthEnd
    const tankRows: IFuelMonthEndTankRow[] = close.tankRows.map((r) => ({
        id: r.id,
        tank: r.tank,
        openingL: r.openingL,
        purchasesL: r.purchasesL,
        issuesL: r.issuesL,
        transfersL: r.transfersL,
        closingL: r.closingL,
    }))

    const rechargeRows: IFuelContractorRechargeRow[] =
        contractorStatement?.rows.map((r) => ({
            id: r.id,
            contractor: r.contractor,
            litres: r.litres,
            amountGhs: r.amountGhs,
            status: r.status,
        })) ?? []

    const tankColumns: IHonestTableColumn<IFuelMonthEndTankRow>[] = [
        {
            id: 'tank',
            header: 'Tank',
            sticky: true,
            minWidth: '9rem',
            cell: (item) => <StatementCellText $em>{item.tank}</StatementCellText>,
        },
        {
            id: 'opening',
            header: 'Opening',
            align: 'right',
            cell: (item) => <StatementCellText>{`${fmt(item.openingL)} L`}</StatementCellText>,
        },
        {
            id: 'purchases',
            header: 'Purchases',
            align: 'right',
            cell: (item) => <StatementCellText>{`${fmt(item.purchasesL)} L`}</StatementCellText>,
        },
        {
            id: 'issues',
            header: 'Issues incl. losses',
            align: 'right',
            cell: (item) => <StatementCellText>{`${fmt(item.issuesL)} L`}</StatementCellText>,
        },
        {
            id: 'transfers',
            header: 'Transfers',
            align: 'right',
            cell: (item) => <StatementCellText>{signedL(item.transfersL)}</StatementCellText>,
        },
        {
            id: 'closing',
            header: 'Closing',
            align: 'right',
            cell: (item) => <StatementCellText $em>{`${fmt(item.closingL)} L`}</StatementCellText>,
        },
    ]

    const rechargeColumns: IHonestTableColumn<IFuelContractorRechargeRow>[] = [
        {
            id: 'contractor',
            header: 'Contractor',
            sticky: true,
            minWidth: '11rem',
            cell: (item) => <StatementCellText $em>{item.contractor}</StatementCellText>,
        },
        {
            id: 'litres',
            header: 'Fuel drawn',
            align: 'right',
            cell: (item) => <StatementCellText>{`${fmt(item.litres)} L`}</StatementCellText>,
        },
        {
            id: 'amount',
            header: 'Recharge',
            align: 'right',
            cell: (item) => <RechargeAmount>{`GH₵ ${fmt(item.amountGhs)}`}</RechargeAmount>,
        },
        {
            id: 'status',
            header: 'Status',
            cell: (item) => <StatementCellText>{item.status}</StatementCellText>,
        },
    ]

    const isSignedOff = close.status === 2

    return (
        <Flex column gap='1rem' width='100%'>
            <Container>
                <ContainerHeader>
                        <TitleText>{`Site statement — ${close.monthLabel} close`}</TitleText>
                </ContainerHeader>
                <ContainerContent gap='0.75rem'>
                    <LedgerWrap>
                        <LedgerRow>
                            <LedgerOp />
                            <LedgerLabel>Opening stock</LedgerLabel>
                            <LedgerValue>{`${fmt(close.openingL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow>
                            <LedgerOp>+</LedgerOp>
                            <LedgerLabel>Purchases</LedgerLabel>
                            <LedgerValue>{`${fmt(close.purchasesL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow>
                            <LedgerOp>−</LedgerOp>
                            <LedgerLabel>Issues</LedgerLabel>
                            <LedgerValue>{`${fmt(close.issuesL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow>
                            <LedgerOp>±</LedgerOp>
                            <LedgerLabel>Transfers net</LedgerLabel>
                            <LedgerValue>{`${fmt(close.transfersNetL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow>
                            <LedgerOp>−</LedgerOp>
                            <LedgerLabel>Losses — closed cases</LedgerLabel>
                            <LedgerValue>{`${fmt(close.lossesClosedL)} L`}</LedgerValue>
                        </LedgerRow>
                        <LedgerRow $em>
                            <LedgerOp>=</LedgerOp>
                            <LedgerLabel>Closing book stock</LedgerLabel>
                            <LedgerValue $em>{`${fmt(close.closingL)} L`}</LedgerValue>
                        </LedgerRow>
                        <MemoRow>
                            <MemoHeadline>
                                {`Pending write-off — ${fmt(close.pendingWriteOffL)} L (GH₵ ${fmt(close.pendingWriteOffGhs)})`}
                            </MemoHeadline>
                            {close.memoNote && <MemoNote>{close.memoNote}</MemoNote>}
                        </MemoRow>
                    </LedgerWrap>
                    {close.continuity.note && <ContinuityNote>{close.continuity.note}</ContinuityNote>}
                </ContainerContent>
            </Container>

            <Container>
                <ContainerHeader>
                    <TitleText>{`Per-tank statements — ${close.monthLabel}`}</TitleText>
                </ContainerHeader>
                <ContainerContent gap='0.75rem'>
                    <HonestTable columns={tankColumns} data={tankRows} keyField='id' emptyMessage='No tank statements yet.' />
                </ContainerContent>
            </Container>

            <Container>
                <ContainerHeader>
                    <TitleText>Contractor fuel</TitleText>
                </ContainerHeader>
                <ContainerContent gap='0.75rem'>
                    <HonestTable
                        columns={rechargeColumns}
                        data={rechargeRows}
                        keyField='id'
                        emptyMessage='No contractor fuel drawn this month.'
                    />
                    <SignOffWrap>
                        {isSignedOff && close.signOff ? (
                            <SignedText>{`✓ Signed off by ${close.signOff.by} · ${close.signOff.at}`}</SignedText>
                        ) : (
                            <>
                                <OtherText grey>Prepared — awaiting sign-off</OtherText>
                                <Button size='sm' variant='primary' onClick={handleSignOff}>Sign off month-end</Button>
                            </>
                        )}
                    </SignOffWrap>
                </ContainerContent>
            </Container>
        </Flex>
    )
}
